// Light and finish (2026-10-26, graphics pass checkpoint 1; tinwhistlegames-ops docs/games/footies/GRAPHICS-PASS.md A).
// A high warm sun and a cool sky fill, ACES, a sky dome and fog so the table fades into the distance. Two tiers, from
// the Graphics setting: Best adds one sun shadow map (PCF soft, following the camera, snapped to its texels so it
// doesn't crawl as the camera drifts) and draws the scene into an HDR target, then a quarter-size bloom (bright pass
// and two blurs, threshold high) and one fused pass for bloom, tone map, saturation and vignette. Light draws straight
// to the screen with tone mapping in each material: no shadow map, no post. The sun's shadow and the post passes are
// compiled at load (warm), the way Sheep Tag learned to (its render/scene.ts warmShadows).
// Also here: the GLSL the world's materials share (noise, the enamel lift on soldiers, cut stone).
// 2026-10-26 (more realistic, the look of a photo of a real diorama): a sky light map every glossy material reflects
// (environment), carved stone with real relief, the tilt-shift of a macro lens on Best and Ultra, and Ultra, a desktop
// tier with a 4096 shadow, softer shadow edges, screen-space contact shade and a half-size bloom.
import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { ClassId } from '../sim/defs';
import { SIZE } from '../sim/sim';
import { PALETTE } from './palette';

export type Tier = 'ultra' | 'best' | 'light';
/** What each tier turns on. Light keeps the discs, blob shadows, the board and the sky's reflections; it drops what
 *  costs a pass. Ultra (capable desktops only, pickTier) spends more on the same chain: shadow resolution and soft
 *  edges, contact shade (ssao), bloom at half size instead of a quarter. */
export const TIERS: Record<Tier, { shadows: boolean; post: boolean; maxRatio: number; shadowSize: number; bloomDiv: number; dof: boolean; ssao: boolean }> = {
  ultra: { shadows: true, post: true, maxRatio: 2, shadowSize: 4096, bloomDiv: 2, dof: true, ssao: true },
  best: { shadows: true, post: true, maxRatio: 2, shadowSize: 2048, bloomDiv: 4, dof: true, ssao: false },
  light: { shadows: false, post: false, maxRatio: 1, shadowSize: 2048, bloomDiv: 4, dof: false, ssao: false },
};
export const SHADOW_SIZE = 2048;

/** A desktop GPU that Ultra fits (a discrete card or Apple's larger chips), by the renderer string WebGL reports. */
const STRONG_GPU = /\b(RTX|GTX 1[0-9]{3}|RX ?[5-9][0-9]{2,3}|Radeon Pro|Arc A[0-9]{3}|Apple M[1-9] (Pro|Max|Ultra))\b/i;
const SOFT_GL = /SwiftShader|llvmpipe|Software|Basic Render/i;
/** The tier a session starts on. ?tier= wins (ultra, best or light); the Graphics setting's Fast is Light; Best is
 *  Ultra on a desktop with a strong GPU, else Best. A phone or tablet never gets Ultra, even when asked. */
export function pickTier(best: boolean, asked: string | null, gpu: string, mobile: boolean, hdr = true): Tier {
  if (asked === 'light' || !best || !hdr) return 'light'; // Best and Ultra draw into a half-float target
  if (mobile || SOFT_GL.test(gpu)) return 'best';
  if (asked === 'ultra') return 'ultra';
  return asked !== 'best' && STRONG_GPU.test(gpu) ? 'ultra' : 'best';
}
/** Whether this context can render into half-float targets (PMREM's and Best's HDR target need it; WebGL2 makes it an
 *  extension, which a few older phones lack). Without it: no light map, and Light only (Astra, 2026-10-26). */
export const hdrTargets = (r: THREE.WebGLRenderer) => r.extensions.has('EXT_color_buffer_float') || r.extensions.has('EXT_color_buffer_half_float');
/** Phones and tablets, including an iPad that asks for the desktop site (a Mac with a touch screen). */
export const isMobile = (ua: string, touchPoints: number) => /Mobi|Android|iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
/** The GPU's name as WebGL reports it ('' when hidden). */
export function gpuName(r: THREE.WebGLRenderer) {
  const gl = r.getContext(), x = gl.getExtension('WEBGL_debug_renderer_info');
  return String(gl.getParameter(x ? x.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? '');
}
/** Where the sun sits, as a direction (toward it): high, from the left and a little in front, so shadows fall back and
 *  to the right of what casts them and every soldier's front is lit. */
export const SUN_DIR = PALETTE.sun.dir;
/** The tallest caster (a top-tier barracks with its roof and flag), so its shadow is inside the map. */
const CAST_H = 13;
/** The sky at the horizon (the fog fades into it) and overhead (palette.ts). */
const HORIZON = PALETTE.sky.horizon, ZENITH = PALETTE.sky.zenith;

/** Uniforms every world material shares: the clock, and uFine (1 on Best and Ultra, 0 on Light: the fine relief and
 *  sheen a material can skip to keep Light as fast as main). The rest sky.ts paints every frame (2026-10-26, seasons,
 *  weather and time of day): snow lying (uCover), wet ground (uWet), rain falling (uRain, ripples), how far the lights
 *  are on (uLamps: dusk and night, the team discs' glow rides it), the river's ice (uIce, winter) and the share of the
 *  verge's flowers out (uFlowers). */
export const shared = { uTime: { value: 0 }, uFine: { value: 1 }, uCover: { value: 0 }, uWet: { value: 0 }, uRain: { value: 0 }, uLamps: { value: 0 }, uIce: { value: 0 }, uFlowers: { value: 0.65 }, uDiscGlow: { value: 0 } };

/** Value noise and a hash, small and portable. Every lattice corner goes through floor() of its own before the hash:
 *  a shader compiler may fold (i + 1) * k into i * k + k, whose rounding differs from the next cell's own corner, and
 *  on the desktop GPU (ANGLE on D3D12) that drew hard-edged squares across the sand (2026-10-26; SwiftShader, which
 *  does not fold, showed none). The hash keeps its numbers small for the same reason (Dave Hoskins' hash without sine). */
export const NOISE_GLSL = `
float lkHash(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float lkNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  float a = lkHash(floor(i + 0.5)), b = lkHash(floor(i + vec2(1.5, 0.5))), c = lkHash(floor(i + vec2(0.5, 1.5))), d = lkHash(floor(i + 1.5));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float lkFbm(vec2 p) { return (lkNoise(p) * 0.5 + lkNoise(p * 2.03 + 1.7) * 0.25 + lkNoise(p * 4.01 + 3.1) * 0.125) / 0.875; }
// Relief from a height: the surface normal tilted by how the height changes across the pixel (three's bump mapping,
// perturbNormalArb, with the height computed in the shader instead of read from a map).
vec3 lkBump(vec3 pos, vec3 n, float h) {
  vec3 sx = dFdx(pos), sy = dFdy(pos), r1 = cross(sy, n), r2 = cross(n, sx);
  float det = dot(sx, r1);
  vec3 g = sign(det) * (dFdx(h) * r1 + dFdy(h) * r2);
  return normalize(abs(det) * n - g);
}
// Rain rings at world xz (meters): each cell rings now and then, at its own spot and moment, so it never reads as a grid
// (Sheep Tag's lesson, 2026-09-30). amt is how hard it rains.
float lkRipple(vec2 w, float t, float amt) {
  vec2 rp = w * 1.6, cell = floor(rp);
  float cyc = t * 0.9 + lkHash(cell) * 13.0, ph = fract(cyc), k = floor(cyc);
  float live = step(lkHash(cell + k * 1.37), 0.22 + 0.33 * amt);
  vec2 at = 0.2 + 0.6 * vec2(lkHash(cell + k + 3.1), lkHash(cell + k + 7.7));
  float rd = length(fract(rp) - at);
  return smoothstep(0.04, 0.0, abs(rd - ph * 0.32)) * (1.0 - ph) * (1.0 - ph) * live * amt;
}`;

/** Snow and wet for a world material (2026-10-26; Sheep Tag's fow.ts weather patch): call it last in onBeforeCompile.
 *  Runs after the material's own color and relief, from the shaded normal in view space and the pixel's world spot
 *  (both from three's own varyings, so any Lambert, Phong or Standard material takes it with no vertex changes):
 *  settled snow (shared.uCover) whitens what faces up, in drifts; wet ground (uWet) darkens, more on the flats, and
 *  picks up a little of the sky. A material can thin the snow with `#define LK_SNOW_MUL <float>` in its own code before
 *  the lights (a trodden road). Skipped in one branch on a uniform when the ground is dry and bare. */
export function weathered(sh: THREE.WebGLProgramParametersWithUniforms) {
  Object.assign(sh.uniforms, { uCover: shared.uCover, uWet: shared.uWet, uSnowCol: { value: PALETTE.snow }, uSkyCol: { value: PALETTE.sky.horizon } });
  const noise = sh.fragmentShader.includes('float lkNoise') ? '' : NOISE_GLSL;
  sh.fragmentShader = sh.fragmentShader
    .replace('#include <common>', `#include <common>\nuniform float uCover; uniform float uWet; uniform vec3 uSnowCol; uniform vec3 uSkyCol;${noise}`)
    .replace('#include <emissivemap_fragment>', `float lkWetK = 0.0;
  if (uCover + uWet > 0.003) {
    vec3 lkW = cameraPosition + (vec4(-vViewPosition, 0.0) * viewMatrix).xyz;
    float lkUp = clamp(dot(normal, normalize((viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz)), 0.0, 1.0);
    float lkDrift = lkNoise(lkW.xz * 0.45) * 0.6 + lkNoise(lkW.xz * 1.7 + 3.0) * 0.4;
    float lkSnow = uCover * smoothstep(0.35, 0.9, lkUp * 0.8 + lkDrift * 0.45 + uCover * 0.35 - 0.3);
#ifdef LK_SNOW_MUL
    lkSnow *= LK_SNOW_MUL;
#endif
    diffuseColor.rgb *= 1.0 - 0.3 * uWet * (0.35 + 0.65 * lkUp);
    diffuseColor.rgb = mix(diffuseColor.rgb, uSnowCol * (0.93 + 0.07 * lkDrift), clamp(lkSnow, 0.0, 0.97));
    lkWetK = uWet * lkUp * (1.0 - lkSnow);
  }
  #include <emissivemap_fragment>`)
    .replace('#include <opaque_fragment>', 'outgoingLight += uSkyCol * lkWetK * 0.06;\n  #include <opaque_fragment>');
}
/** A plain material (no shader of its own) that takes snow and wet: a roof. snow under 1 keeps that share of the
 *  snow (a team's roof keeps its color). */
export function weatheredMaterial<M extends THREE.Material>(m: M, key: string, snow = 1) {
  m.onBeforeCompile = (sh) => { if (snow !== 1) sh.fragmentShader = `#define LK_SNOW_MUL ${snow.toFixed(2)}\n` + sh.fragmentShader; weathered(sh); };
  m.customProgramCacheKey = () => `wx-${key}`;
  return m;
}

/** The enamel lift for soldiers (Phong materials): a floor under the paint's value so black lacquer reads as glossy
 *  black, not a hole, and a cool rim on the silhouette so a dark figure separates from the grass. */
export function enamel(sh: THREE.WebGLProgramParametersWithUniforms) {
  sh.fragmentShader = sh.fragmentShader
    // after the paint and any team repaint (both hook color_fragment), before the lights
    .replace('#include <alphamap_fragment>', 'diffuseColor.rgb = max(diffuseColor.rgb, vec3(0.04, 0.045, 0.06));\n  #include <alphamap_fragment>')
    .replace('#include <opaque_fragment>', `{
    float rim = 1.0 - clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0);
    outgoingLight += vec3(0.24, 0.28, 0.36) * rim * rim * rim;
  }
  #include <opaque_fragment>`);
}
/** Gloss for the enamel: Phong's highlight, tight and not too hot, so many soldiers don't turn to glitter. */
export const ENAMEL = { shininess: 45, specular: 0x2a2a2a };

/** How a stone material lays out its blocks: `block` meters long (courses half that high); `round` wraps the courses
 *  round a tower standing at x, z (a whole number of blocks a ring, so no seam); `setts` paves small cobbles along the
 *  geometry's uv (meters along and across a bridge deck). */
export type StoneOpts = { block?: number; round?: { x: number; z: number; r: number }; setts?: boolean };

/** Cut stone (the barracks, the center, the bridges): courses of blocks of uneven length, each block its own shade
 *  and a little warmer or cooler, its face chiseled, the mortar sunk between (relief from lkBump, so the sun picks out
 *  every joint). Lambert, so it takes the sun and its shadow. */
export function stoneMaterial(color: THREE.Color, o: StoneOpts = {}) {
  const mat = new THREE.MeshLambertMaterial();
  mat.color = color; // the palette's own Color, so a change there shows
  const b = o.block ?? 1.1, B = b.toFixed(3), H = (b * 0.5).toFixed(3);
  const ring = o.round ? Math.max(8, Math.round((Math.PI * 2 * o.round.r) / b)) : 0;
  // where on the block pattern this pixel is: courses run level on walls, on the deck along the road
  const at = o.setts ? `vec2 p = vStoneUv / vec2(${(b * 0.55).toFixed(3)}, ${(b * 0.4).toFixed(3)});`
    : o.round ? `vec2 d = vStoneP.xz - vec2(${o.round.x.toFixed(2)}, ${o.round.z.toFixed(2)});
    vec2 p = abs(vStoneN.y) > 0.7 ? vStoneP.xz / vec2(${B}, ${H}) : vec2((atan(d.y, d.x) / 6.2831853 + 0.5) * ${ring.toFixed(1)}, vStoneP.y / ${H});`
    : `vec3 n = abs(vStoneN);
    vec2 p = (n.y > 0.7 ? vStoneP.xz : (n.x > n.z ? vStoneP.zy : vStoneP.xy)) / vec2(${B}, ${H});`;
  mat.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vStoneP; varying vec3 vStoneN; varying vec2 vStoneUv;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vStoneP = (modelMatrix * vec4(transformed, 1.0)).xyz; vStoneN = normalize(mat3(modelMatrix) * objectNormal); vStoneUv = uv;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vStoneP; varying vec3 vStoneN; varying vec2 vStoneUv;' + NOISE_GLSL)
      .replace('#include <color_fragment>', `#include <color_fragment>
  float lkH;
  {
    ${at}
    // each course shifted by its own amount, and about half the blocks cut in two somewhere off the middle
    float row = floor(p.y);
    p.x += lkHash(vec2(row, 7.0)) * 0.9;
    float cell = floor(p.x), cut = 0.3 + 0.4 * lkHash(vec2(cell, row + 31.0));
    bool split = lkHash(vec2(cell + 17.0, row)) < 0.45;
    float fx = fract(p.x), fy = fract(p.y);
    float lx = split ? (fx < cut ? fx / cut : (fx - cut) / (1.0 - cut)) : fx;
    vec2 id = vec2(cell * 2.0 + (split && fx >= cut ? 1.0 : 0.0), row);
    float aa = clamp(fwidth(p.y) * 1.5, 0.0, 0.5);
    float jx = 0.05 + aa, jy = 0.09 + aa;
    float m = smoothstep(0.0, jx, lx) * (1.0 - smoothstep(1.0 - jx, 1.0, lx)) * smoothstep(0.0, jy, fy) * (1.0 - smoothstep(1.0 - jy, 1.0, fy));
    // the chiseled face: a slow lump per block and fine pitting, both fading out where a pixel can't show them
    float fine = clamp(1.0 - aa * 2.5, 0.0, 1.0);
    float face = lkNoise(vStoneP.xz * 2.1 + vStoneP.y * 1.7 + id * 3.1) - 0.5, pit = lkNoise((vStoneP.xz + vStoneP.yx) * 9.0) - 0.5;
    lkH = 0.035 * m + fine * (0.012 * face + 0.004 * pit);
    float h = lkHash(id), shade = 0.84 + 0.24 * h + 0.07 * face;
    vec3 tone = mix(vec3(0.95, 0.98, 1.04), vec3(1.05, 1.0, 0.93), lkHash(id + 11.0));
    diffuseColor.rgb *= mix(vec3(0.58), shade * tone, m);
  }`)
      .replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n  normal = lkBump(-vViewPosition, normal, lkH);');
    weathered(sh); // snow on the tops: copings, battlements, string courses, plinths
  };
  mat.customProgramCacheKey = () => `stone-${b}-${o.setts ? 's' : o.round ? `r${ring}` : 'w'}`;
  return mat;
}

/** The team disc (STYLE.md "Bases and movement"): radius and height before a soldier's scale, so his feet stand on
 *  its top. Riders and engines get a wider one. */
export const DISC_R = 0.36, DISC_TOP = 0.075;
export const DISC_SCALE: Record<ClassId, number> = { footman: 1, line: 1, spear: 1, ranged: 1, cavalry: 1.35, siege: 1.7, support: 1, elite: 1.1, hero: 1.1, ram: 1.7 };
/** Discs of the fallen still shrinking away at once (past that, a pop shows no disc). */
export const FADES = 64;

/** Glossy discs for n soldiers: a short round slab with a rolled edge that catches the sun, one instanced draw. */
export function teamDiscs(n: number) {
  const pts = [[DISC_R, 0], [DISC_R, 0.045], [DISC_R - 0.01, 0.065], [DISC_R - 0.03, DISC_TOP], [0, DISC_TOP]].map(([x, y]) => new THREE.Vector2(x, y));
  // Standard, so the enamel mirrors the sky (scene.environment) the way a glossy painted base does in a photo. It glows
  // a little in its own color as the light drops and on snow (shared.uDiscGlow, sky.ts): VISUALS.md's readability rule,
  // the disc carries the team read for the white-clad armies.
  const mat = new THREE.MeshStandardMaterial({ roughness: 0.2, metalness: 0 });
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uDiscGlow = shared.uDiscGlow;
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uDiscGlow;')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n  totalEmissiveRadiance += vColor * uDiscGlow;');
  };
  mat.customProgramCacheKey = () => 'disc';
  const m = new THREE.InstancedMesh(new THREE.LatheGeometry(pts, 28), mat, n);
  m.setColorAt(0, new THREE.Color());
  m.instanceMatrix.setUsage(THREE.DynamicDrawUsage); m.instanceColor!.setUsage(THREE.DynamicDrawUsage);
  m.frustumCulled = false; m.receiveShadow = true; m.count = 0;
  return m;
}

/** A soft round falloff as an alpha map (its green channel): the blob shadows' and the lanterns' pools of light. */
let radial: THREE.DataTexture | null = null;
export function radialTexture() {
  if (radial) return radial;
  const S = 32, data = new Uint8Array(S * S * 4);
  for (let j = 0; j < S; j++) for (let i = 0; i < S; i++) {
    const d = Math.hypot((i + 0.5) / S * 2 - 1, (j + 0.5) / S * 2 - 1), a = Math.max(0, 1 - d) ** 1.6;
    data.set([255, Math.round(a * 255), 255, 255], (j * S + i) * 4);
  }
  radial = new THREE.DataTexture(data, S, S, THREE.RGBAFormat);
  radial.magFilter = radial.minFilter = THREE.LinearFilter; radial.needsUpdate = true;
  return radial;
}

/** Soft round shadows for n soldiers: a flat quad each with a radial falloff, drawn over the board and under the
 *  discs (they write no depth, so a disc covers its middle). */
export function blobShadows(n: number) {
  const mat = Object.assign(new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.55, alphaMap: radialTexture(), depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }), { color: PALETTE.ground.contact });
  const m = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), mat, n);
  m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  m.frustumCulled = false; m.count = 0; m.renderOrder = 1;
  return m;
}

/** The defeat puff: a small cloud of round lumps, unit size (scaled by the soldier). */
export function puffGeometry() {
  const lumps = [[0, 0, 0, 0.42], [0.32, 0.08, 0.1, 0.3], [-0.3, 0.05, -0.08, 0.32], [0.05, 0.28, -0.05, 0.3], [-0.06, 0.02, 0.32, 0.27]];
  return mergeGeometries(lumps.map(([x, y, z, r]) => new THREE.IcosahedronGeometry(r, 1).translate(x, y, z)))!;
}

/** The ground a camera sees, clipped to the board: the box the sun's shadow has to cover. Rays from the view's four
 *  corners to the ground, wherever they meet it (a ray at or above the horizon reaches `far` along the ground). Not
 *  capped short of the ground: on an ultrawide window a corner meets it past 400 m and the cut lost visible shadow
 *  (Astra, 2026-10-26). */
export function viewFootprint(camera: THREE.PerspectiveCamera, out = { x0: 0, z0: 0, x1: 0, z1: 0 }, far = 400) {
  camera.updateMatrixWorld();
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (let k = 0; k < 4; k++) {
    _p.set(k & 1 ? 1 : -1, k & 2 ? 1 : -1, 0.5).unproject(camera);
    _d.copy(_p).sub(camera.position).normalize();
    const t = _d.y < -1e-4 ? -camera.position.y / _d.y : far / Math.max(1e-4, Math.hypot(_d.x, _d.z));
    const x = camera.position.x + _d.x * t, z = camera.position.z + _d.z * t;
    x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z);
  }
  out.x0 = Math.max(-1, x0); out.z0 = Math.max(-1, z0); out.x1 = Math.min(SIZE + 1, x1); out.z1 = Math.min(SIZE + 1, z1);
  return out;
}
const _p = new THREE.Vector3(), _d = new THREE.Vector3(), _c = new THREE.Vector3(), _q = new THREE.Vector3();
const _ax = new THREE.Vector3(), _ay = new THREE.Vector3(), _fp = { x0: 0, z0: 0, x1: 0, z1: 0 };

/** Aims the sun's shadow at a footprint: the box's corners, at the ground and at the tallest caster's height, taken
 *  into the light's own axes. Its size goes up in 8 m steps and its edges sit on whole texels, so as the camera pans
 *  every texel stays on the same patch of ground (no crawling edges). */
export function aimShadow(sun: THREE.DirectionalLight, fp: { x0: number; z0: number; x1: number; z1: number }) {
  // the light's axes, as three's lookAt makes them for the shadow camera (z toward the sun, x level)
  _ax.crossVectors(THREE.Object3D.DEFAULT_UP, SUN_DIR).normalize();
  _ay.crossVectors(SUN_DIR, _ax);
  let lx0 = Infinity, lx1 = -Infinity, ly0 = Infinity, ly1 = -Infinity;
  for (let k = 0; k < 8; k++) {
    _q.set(k & 1 ? fp.x1 : fp.x0, k & 4 ? CAST_H : 0, k & 2 ? fp.z1 : fp.z0);
    const lx = _q.dot(_ax), ly = _q.dot(_ay);
    lx0 = Math.min(lx0, lx); lx1 = Math.max(lx1, lx); ly0 = Math.min(ly0, ly); ly1 = Math.max(ly1, ly);
  }
  const w = Math.ceil((lx1 - lx0 + 2) / 8) * 8, h = Math.ceil((ly1 - ly0 + 2) / 8) * 8;
  const size = sun.shadow.mapSize.x, tx = w / size, ty = h / size;
  const left = Math.floor((lx0 - 1) / tx) * tx, bottom = Math.floor((ly0 - 1) / ty) * ty;
  // the camera sits on the light axis through the box's middle; its window is the snapped box around it
  const mx = Math.round((left + w / 2) / tx) * tx, my = Math.round((bottom + h / 2) / ty) * ty;
  _c.copy(_ax).multiplyScalar(mx).addScaledVector(_ay, my);
  sun.target.position.copy(_c);
  sun.position.copy(_c).addScaledVector(SUN_DIR, 200);
  const cam = sun.shadow.camera;
  cam.left = left - mx; cam.right = left + w - mx; cam.bottom = bottom - my; cam.top = bottom + h - my;
  cam.near = 1; cam.far = 400;
  cam.updateProjectionMatrix();
}

const QUAD_VERT = 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }';
/** Four taps (a 4x4 block at full size) into a smaller target, keeping what is over the threshold: the bloom's bright
 *  pass, and with the threshold below zero the plain downsample the tilt-shift blurs. */
const brightMat = (threshold = 1.5) => new THREE.ShaderMaterial({
  uniforms: { tSrc: { value: null }, uTexel: { value: new THREE.Vector2() }, uThreshold: { value: threshold } },
  vertexShader: QUAD_VERT,
  fragmentShader: `uniform sampler2D tSrc; uniform vec2 uTexel; uniform float uThreshold; varying vec2 vUv;
    void main() {
      vec3 c = (texture2D(tSrc, vUv + uTexel * vec2(-1.0, -1.0)).rgb + texture2D(tSrc, vUv + uTexel * vec2(1.0, -1.0)).rgb
              + texture2D(tSrc, vUv + uTexel * vec2(-1.0, 1.0)).rgb + texture2D(tSrc, vUv + uTexel * vec2(1.0, 1.0)).rgb) * 0.25;
      float l = max(c.r, max(c.g, c.b));
      gl_FragColor = vec4(c * smoothstep(uThreshold, uThreshold + 0.8, l), 1.0);
    }`,
  depthTest: false, depthWrite: false, toneMapped: false,
});
/** A 9-tap Gaussian in five bilinear reads, one direction per pass. */
const blurMat = () => new THREE.ShaderMaterial({
  uniforms: { tSrc: { value: null }, uDir: { value: new THREE.Vector2() } },
  vertexShader: QUAD_VERT,
  fragmentShader: `uniform sampler2D tSrc; uniform vec2 uDir; varying vec2 vUv;
    void main() {
      vec3 c = texture2D(tSrc, vUv).rgb * 0.2270270;
      c += (texture2D(tSrc, vUv + uDir * 1.3846154).rgb + texture2D(tSrc, vUv - uDir * 1.3846154).rgb) * 0.3162162;
      c += (texture2D(tSrc, vUv + uDir * 3.2307692).rgb + texture2D(tSrc, vUv - uDir * 3.2307692).rgb) * 0.0702703;
      gl_FragColor = vec4(c, 1.0);
    }`,
  depthTest: false, depthWrite: false, toneMapped: false,
});
/** Contact shade from the depth buffer (Ultra): for each pixel, how much of the half space over its surface the
 *  nearby depth closes off, from 12 taps on a spiral turned per pixel, within AO_RADIUS meters. Half size. */
const AO_RADIUS = 1.6;
const aoMat = () => new THREE.ShaderMaterial({
  uniforms: { tDepth: { value: null }, uInvProj: { value: new THREE.Matrix4() }, uScale: { value: new THREE.Vector2() }, uTexel: { value: new THREE.Vector2() } },
  vertexShader: QUAD_VERT,
  fragmentShader: `uniform sampler2D tDepth; uniform mat4 uInvProj; uniform vec2 uScale; uniform vec2 uTexel; varying vec2 vUv;
    vec3 at(vec2 uv) { vec4 p = uInvProj * vec4(uv * 2.0 - 1.0, texture2D(tDepth, uv).r * 2.0 - 1.0, 1.0); return p.xyz / p.w; }
    void main() {
      if (texture2D(tDepth, vUv).r >= 1.0) { gl_FragColor = vec4(1.0); return; }
      vec3 p = at(vUv);
      // the surface's normal from the neighbour on each axis whose depth is closer (no false creases on silhouettes)
      vec3 r = at(vUv + vec2(uTexel.x, 0.0)), l = at(vUv - vec2(uTexel.x, 0.0)), u = at(vUv + vec2(0.0, uTexel.y)), d = at(vUv - vec2(0.0, uTexel.y));
      vec3 dx = abs(r.z - p.z) < abs(p.z - l.z) ? r - p : p - l, dy = abs(u.z - p.z) < abs(p.z - d.z) ? u - p : p - d;
      vec3 n = normalize(cross(dx, dy));
      float ign = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
      vec2 rad = uScale * ${AO_RADIUS.toFixed(2)} / -p.z;
      float occ = 0.0;
      for (int k = 0; k < 12; k++) {
        float t = (float(k) + ign) / 12.0, a = (float(k) * 2.39996 + ign * 6.2831853);
        vec3 q = at(vUv + vec2(cos(a), sin(a)) * rad * t) - p;
        float len = length(q);
        occ += max(0.0, dot(q, n) / max(len, 1e-4) - 0.15) * (1.0 - smoothstep(${(AO_RADIUS * 0.5).toFixed(2)}, ${AO_RADIUS.toFixed(2)}, len));
      }
      float ao = clamp(1.0 - occ / 12.0 * 1.6, 0.0, 1.0);
      gl_FragColor = vec4(ao, ao, ao, 1.0);
    }`,
  depthTest: false, depthWrite: false, toneMapped: false,
});
/** The final pass's fragment source (tests/look.test.ts checks the order of its steps). */
export const FINAL_FRAG = () => finalMat().fragmentShader;
/** The one pass to the screen: the tilt-shift (a macro photo's focus: a sharp band through the
 *  middle of the view, the top, which is farther away, softer than the bottom), contact shade (Ultra), bloom added, ACES (three's
 *  tonemapping chunk, the renderer's exposure), then the grade: a saturation lift, a touch of warmth, a soft vignette;
 *  then sRGB. A missing input is three's empty texture and its weight is zero. */
const finalMat = () => new THREE.ShaderMaterial({
  uniforms: { tScene: { value: null }, tBloom: { value: null }, tDof: { value: null }, tAo: { value: null }, uBloom: { value: 0.55 }, uSat: { value: 1.12 }, uVig: { value: 0.32 }, uDof: { value: 0 }, uAo: { value: 0 } },
  vertexShader: QUAD_VERT,
  fragmentShader: `uniform sampler2D tScene; uniform sampler2D tBloom; uniform sampler2D tDof; uniform sampler2D tAo;
    uniform float uBloom; uniform float uSat; uniform float uVig; uniform float uDof; uniform float uAo; varying vec2 vUv;
    void main() {
      float y = vUv.y - 0.46, coc = smoothstep(0.2, 0.52, abs(y)) * (y > 0.0 ? 1.0 : 0.7);
      vec3 c = mix(texture2D(tScene, vUv).rgb, texture2D(tDof, vUv).rgb, coc * uDof);
      // contact shade after the blend, so it holds in the blurred bands too (Astra, 2026-10-26)
      c *= mix(1.0, texture2D(tAo, vUv).r, uAo);
      gl_FragColor = vec4(c + texture2D(tBloom, vUv).rgb * uBloom, 1.0);
      #include <tonemapping_fragment>
      float l = dot(gl_FragColor.rgb, vec3(0.2126, 0.7152, 0.0722));
      gl_FragColor.rgb = mix(vec3(l), gl_FragColor.rgb, uSat) * vec3(1.02, 1.0, 0.97);
      vec2 d = vUv - 0.5;
      gl_FragColor.rgb *= 1.0 - uVig * smoothstep(0.35, 0.85, length(d * vec2(1.1, 1.25)));
      #include <colorspace_fragment>
    }`,
  depthTest: false, depthWrite: false,
});
/** A dome around the camera, horizon to zenith, under the same tone map as the world. */
function skyDome() {
  const mat = new THREE.ShaderMaterial({
    uniforms: { uHorizon: { value: HORIZON }, uZenith: { value: ZENITH } },
    vertexShader: 'varying vec3 vDir; void main() { vDir = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `uniform vec3 uHorizon; uniform vec3 uZenith; varying vec3 vDir;
      void main() { float h = max(normalize(vDir).y, 0.0); gl_FragColor = vec4(mix(uHorizon, uZenith, pow(h, 0.55)), 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    side: THREE.BackSide, depthWrite: false, fog: false,
  });
  const dome = new THREE.Mesh(new THREE.SphereGeometry(800, 24, 12), mat);
  dome.renderOrder = -10; dome.frustumCulled = false;
  return dome;
}

/** What makes one shadow caster's depth program differ from another's in three r170 (from Sheep Tag's scene.ts
 *  shadowVariant): two casters with the same key share a program, so warming one warms both. */
export function shadowVariant(m: THREE.Material, o: THREE.Object3D) {
  const mm = m as THREE.Material & { map?: unknown; alphaMap?: unknown; vertexColors?: boolean };
  const om = o as THREE.Object3D & { isSkinnedMesh?: boolean; isInstancedMesh?: boolean; isBatchedMesh?: boolean; instanceColor?: unknown; morphTexture?: unknown; geometry?: THREE.BufferGeometry };
  const morph = om.geometry?.morphAttributes ?? {};
  return [m.uuid, m.side, !!mm.map, m.alphaTest > 0, !!mm.alphaMap, !!mm.vertexColors, !!om.isSkinnedMesh, !!om.isInstancedMesh,
    !!om.instanceColor, !!om.morphTexture, !!om.isBatchedMesh, Object.keys(morph).join('+'), (morph.position ?? []).length, o.receiveShadow].join('|');
}


/** Soft clouds in the light map: azimuth (radians) and height (the sine of the elevation). */
const CLOUDS = [[0.4, 0.32], [1.3, 0.2], [2.2, 0.42], [3.3, 0.26], [4.4, 0.36], [5.4, 0.16], [2.9, 0.62]];
/** The sky the glossy things mirror and Standard materials take their fill from (linear radiance, before
 *  scene.environmentIntensity): the dome's colors, brighter haze toward the horizon, a warm glow round the sun, a few
 *  soft clouds, and below the horizon the green felt of the board. The sun itself stays out (its DirectionalLight
 *  already gives every highlight). Dim overall, since the hemisphere light still fills Standard materials too. */
export function skyRadiance(d: THREE.Vector3, out: THREE.Color) {
  const y = d.y;
  // the board below (the fill's bounce color, so it darkens at night and whitens under snow), then the sky; the sun's
  // glow and the clouds in the hour's own light (sky.ts paints all of these), so the map holds at dusk and at night
  if (y < 0) return out.copy(PALETTE.fill.ground).multiplyScalar(0.35).lerp(_h.copy(HORIZON).multiplyScalar(0.45), Math.exp(y * 9));
  out.copy(HORIZON).lerp(ZENITH, Math.pow(y, 0.5)).multiplyScalar(0.3 + 0.32 * Math.pow(1 - y, 5));
  const s = Math.max(0, d.dot(SUN_DIR)) ** 10 * 0.9 * PALETTE.sun.intensity / 2;
  out.r += PALETTE.sun.color.r * s; out.g += PALETTE.sun.color.g * s; out.b += PALETTE.sun.color.b * s;
  for (const [az, el] of CLOUDS) {
    const ce = Math.sqrt(1 - el * el), dot = d.x * Math.cos(az) * ce + d.y * el + d.z * Math.sin(az) * ce;
    const k = 0.66 * THREE.MathUtils.smoothstep(dot, 0.955, 0.995);
    out.r += k * HORIZON.r; out.g += k * HORIZON.g; out.b += k * HORIZON.b;
  }
  return out;
}
const _h = new THREE.Color();

/** The sky light map: the sky (skyRadiance) as a small equirectangular image (256 x 128, half floats) through PMREM,
 *  about 0.7 MB on the GPU, nothing downloaded. Made whole at load; when the sky has moved on (Stage.light) it is drawn
 *  again a few rows a frame (all of it at once was about 20 ms of CPU on the box, a hitch every 15 s or so) and run
 *  through PMREM into the same target when the last row is in, so nothing is allocated after load. */
const ENV_W = 256, ENV_H = 128;
export class SkyMap {
  private data = new Uint16Array(ENV_W * ENV_H * 4);
  private img = new THREE.DataTexture(this.data, ENV_W, ENV_H, THREE.RGBAFormat, THREE.HalfFloatType);
  private pmrem: THREE.PMREMGenerator;
  private rt: THREE.WebGLRenderTarget;
  /** The next row to draw, or -1 when the map is up to date. */
  private row = -1;
  constructor(renderer: THREE.WebGLRenderer) {
    this.img.mapping = THREE.EquirectangularReflectionMapping; this.img.magFilter = this.img.minFilter = THREE.LinearFilter;
    this.pmrem = new THREE.PMREMGenerator(renderer);
    this.rows(0, ENV_H); this.img.needsUpdate = true;
    this.rt = this.pmrem.fromEquirectangular(this.img);
  }
  get texture() { return this.rt.texture; }
  /** Starts drawing it again (one under way starts over, from the newest sky). */
  remake() { this.row = 0; }
  /** Draws up to n more rows; with the last one in, the map is remade. */
  step(n: number) {
    if (this.row < 0) return;
    const to = Math.min(ENV_H, this.row + n);
    this.rows(this.row, to);
    this.row = to < ENV_H ? to : -1;
    if (this.row < 0) { this.img.needsUpdate = true; this.pmrem.fromEquirectangular(this.img, this.rt); }
  }
  private rows(j0: number, j1: number) {
    const d = _ed, c = _ec, data = this.data, h = THREE.DataUtils.toHalfFloat;
    for (let j = j0; j < j1; j++) for (let i = 0; i < ENV_W; i++) {
      // three's equirectUv: u = atan(z, x) / 2 pi + 0.5, v = asin(y) / pi + 0.5, the first row at v = 0
      const phi = ((i + 0.5) / ENV_W - 0.5) * Math.PI * 2, th = ((j + 0.5) / ENV_H - 0.5) * Math.PI;
      d.set(Math.cos(phi) * Math.cos(th), Math.sin(th), Math.sin(phi) * Math.cos(th));
      skyRadiance(d, c);
      const k = (j * ENV_W + i) * 4;
      data[k] = h(c.r); data[k + 1] = h(c.g); data[k + 2] = h(c.b); data[k + 3] = h(1);
    }
  }
}
const _ed = new THREE.Vector3(), _ec = new THREE.Color();

/** What the Stage takes from the sky each frame (sky.ts paint): the sun's and the fill's strength, exposure, how dark
 *  the sun's shadow is (overcast softens it), how far the fog stands (rain and snow pull it in), the grade's
 *  saturation, the light map's strength, and a key that changes when the light map needs remaking. */
export type StageLight = { sun: number; fill: number; exposure: number; shadow: number; fog: number; sat: number; env: number; envKey: string };

type Targets = { rt: THREE.WebGLRenderTarget | null; bloomA: THREE.WebGLRenderTarget | null; bloomB: THREE.WebGLRenderTarget | null;
  dofA: THREE.WebGLRenderTarget | null; dofB: THREE.WebGLRenderTarget | null; aoA: THREE.WebGLRenderTarget | null; aoB: THREE.WebGLRenderTarget | null };
const HALF = { type: THREE.HalfFloatType, depthBuffer: false } as const, BYTE = { depthBuffer: false } as const;

export class Stage {
  readonly sun = new THREE.DirectionalLight(0xffffff, PALETTE.sun.intensity);
  readonly hemi = new THREE.HemisphereLight(0xffffff, 0xffffff, PALETTE.fill.intensity);
  readonly sky = skyDome();
  tier: Tier = 'light';
  /** The tilt-shift on Best and Ultra; ?dof=0 (or a setting) turns it off. */
  dof = true;
  private t: Targets = { rt: null, bloomA: null, bloomB: null, dofA: null, dofB: null, aoA: null, aoB: null };
  private bright = brightMat();
  private down = brightMat(-10);
  private blur = blurMat();
  private ao = aoMat();
  private final = finalMat();
  private quad = new FullScreenQuad(this.final);
  private fog = Object.assign(new THREE.Fog(0xffffff, 140, 400), { color: HORIZON });
  /** The sky light map: Best and Ultra only (Light stays as main was, with none, and as fast). */
  private env: THREE.Texture | null = null;
  private skyMap: SkyMap | null = null;
  private envKey = '';
  /** Rows of the light map drawn a frame while it is remade (capture mode takes it all at once: a still is one frame). */
  envRows = 4;
  /** Rain and snow pull the fog in (Stage.light). */
  private fogK = 1;
  /** What only Best and Ultra draw (full trees, grass) and what only Light draws (lite trees). */
  private tiered: { best: THREE.Object3D[]; light: THREE.Object3D[] } = { best: [], light: [] };

  constructor(readonly renderer: THREE.WebGLRenderer, readonly scene: THREE.Scene, readonly camera: THREE.PerspectiveCamera, tier: Tier) {
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.info.autoReset = false; // a frame is several draws (shadow, scene, bloom, final): count them all (prof)
    // the lights wear the palette's Colors (palette.ts), so a change there shows on the next frame
    this.sun.color = PALETTE.sun.color; this.hemi.color = PALETTE.fill.sky; this.hemi.groundColor = PALETTE.fill.ground;
    scene.background = HORIZON;
    scene.fog = this.fog;
    if (hdrTargets(renderer)) { this.skyMap = new SkyMap(renderer); this.env = this.skyMap.texture; } // setTier hangs it on the scene
    this.sun.shadow.bias = -0.0005;
    this.sun.shadow.normalBias = 0.04;
    scene.add(this.hemi, this.sun, this.sun.target, this.sky);
    this.setTier(tier);
  }

  setTier(t: Tier) {
    const was = this.renderer.shadowMap.type;
    this.tier = t;
    const on = TIERS[t], r = this.renderer;
    // castShadow too, not only the map: it is part of the lights' state, so every lit program picks the change up
    r.shadowMap.enabled = on.shadows;
    this.sun.castShadow = on.shadows;
    // Ultra: plain PCF with a wider kernel on a map twice the size, so edges are both finer and softer
    r.shadowMap.type = t === 'ultra' ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;
    this.sun.shadow.radius = t === 'ultra' ? 3 : 1;
    if (this.sun.shadow.mapSize.x !== on.shadowSize) {
      this.sun.shadow.mapSize.set(on.shadowSize, on.shadowSize);
      this.sun.shadow.map?.dispose(); this.sun.shadow.map = null; // three makes it again at the new size
    }
    // the shadow type is in every lit program's key, but three doesn't notice it change on its own
    if (r.shadowMap.type !== was) this.scene.traverse((o) => { const m = (o as THREE.Mesh).material; for (const x of Array.isArray(m) ? m : m ? [m] : []) x.needsUpdate = true; });
    for (const k of Object.keys(this.t) as (keyof Targets)[]) { this.t[k]?.dispose(); this.t[k] = null; }
    this.scene.environment = t === 'light' ? null : this.env;
    shared.uFine.value = t === 'light' ? 0 : 1;
    this.showTiered();
    this.resize();
  }

  /** Hands the Stage objects only Best and Ultra draw, and objects only Light draws; shown or hidden now and on every
   *  tier change. */
  addTiered(o: { best: THREE.Object3D[]; light: THREE.Object3D[] }) {
    this.tiered.best.push(...o.best); this.tiered.light.push(...o.light);
    this.showTiered();
  }
  private showTiered() {
    const light = this.tier === 'light';
    for (const o of this.tiered.best) o.visible = !light;
    for (const o of this.tiered.light) o.visible = light;
  }

  /** The window or pixel ratio changed (a resize also comes when the window moves to a screen of another density):
   *  the canvas, the camera and the post targets follow. */
  resize() {
    const r = this.renderer, on = TIERS[this.tier], ratio = Math.min(devicePixelRatio, on.maxRatio);
    if (r.getPixelRatio() !== ratio) r.setPixelRatio(ratio);
    r.setSize(innerWidth, innerHeight);
    this.camera.aspect = innerWidth / innerHeight;
    this.camera.updateProjectionMatrix();
    if (!on.post) return;
    const size = r.getDrawingBufferSize(new THREE.Vector2()), w = Math.max(1, size.x), h = Math.max(1, size.y);
    const part = (div: number) => [Math.max(1, Math.ceil(w / div)), Math.max(1, Math.ceil(h / div))] as const;
    // MSAA in the HDR target, except at a pixel ratio of 2 where the pixels are the anti-aliasing (VISUALS.md section 4)
    const samples = ratio >= 2 ? 0 : 4, t = this.t;
    if (t.rt && t.rt.samples !== samples) { t.rt.dispose(); t.rt = null; }
    // Ultra keeps the depth as a texture for the contact shade
    t.rt ??= new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, samples, depthTexture: on.ssao ? new THREE.DepthTexture(w, h) : undefined });
    t.rt.setSize(w, h);
    const fit = (k: keyof Targets, [pw, ph]: readonly [number, number], o: object) => { const x = (t[k] ??= new THREE.WebGLRenderTarget(pw, ph, o)); x.setSize(pw, ph); };
    fit('bloomA', part(on.bloomDiv), HALF); fit('bloomB', part(on.bloomDiv), HALF);
    if (on.dof) { fit('dofA', part(4), HALF); fit('dofB', part(4), HALF); }
    if (on.ssao) { fit('aoA', part(2), BYTE); fit('aoB', part(2), BYTE); }
    this.bright.uniforms.uTexel.value.set(1 / w, 1 / h);
    this.down.uniforms.uTexel.value.set(1 / w, 1 / h);
    this.ao.uniforms.uTexel.value.set(1 / w, 1 / h);
  }

  /** The hour's and the weather's light, once a frame (sky.ts paint has set the palette's colors). The light map is
   *  remade only when its key moves on (the sky's hour, cloud or snow by a step), and only where it is drawn. */
  light(l: StageLight) {
    this.sun.intensity = l.sun; this.hemi.intensity = l.fill;
    this.renderer.toneMappingExposure = l.exposure;
    this.sun.shadow.intensity = l.shadow;
    this.fogK = l.fog;
    this.final.uniforms.uSat.value = l.sat;
    this.scene.environmentIntensity = l.env;
    if (this.skyMap && this.tier !== 'light') {
      if (l.envKey !== this.envKey) { this.envKey = l.envKey; this.skyMap.remake(); }
      this.skyMap.step(this.envRows);
    }
  }

  /** Once a frame, after the camera moves: the sun's shadow over what it sees, the fog by its distance, the dome on it. */
  follow(dist: number) {
    this.sky.position.copy(this.camera.position);
    // the fog starts past the far edge of a close view and closes on the table beyond the board
    this.fog.near = dist * 1.4 * this.fogK; this.fog.far = dist * 3.6 * this.fogK;
    if (this.sun.castShadow) aimShadow(this.sun, viewFootprint(this.camera, _fp));
    else { this.sun.target.position.set(SIZE / 2, 0, SIZE / 2); this.sun.position.copy(SUN_DIR).multiplyScalar(200).add(this.sun.target.position); }
  }

  render(time: number) {
    shared.uTime.value = time;
    const r = this.renderer, t = this.t, on = TIERS[this.tier];
    r.info.reset();
    if (!t.rt) { r.setRenderTarget(null); r.render(this.scene, this.camera); return; }
    r.setRenderTarget(t.rt);
    r.render(this.scene, this.camera);
    const f = this.final.uniforms;
    if (on.ssao && t.aoA && t.aoB) {
      const u = this.ao.uniforms;
      u.tDepth.value = t.rt.depthTexture;
      u.uInvProj.value.copy(this.camera.projectionMatrixInverse);
      // a meter at a meter's distance, in uv, on each axis
      u.uScale.value.set(this.camera.projectionMatrix.elements[0] * 0.5, this.camera.projectionMatrix.elements[5] * 0.5);
      this.quad.material = this.ao; r.setRenderTarget(t.aoA); this.quad.render(r);
      this.blurPass(t.aoA, t.aoB);
      f.tAo.value = t.aoA.texture; f.uAo.value = 0.85;
    } else { f.tAo.value = null; f.uAo.value = 0; }
    if (on.dof && this.dof && t.dofA && t.dofB) {
      this.pass(this.down, t.rt.texture, t.dofA);
      this.blurPass(t.dofA, t.dofB, 1.6);
      f.tDof.value = t.dofA.texture; f.uDof.value = 0.75;
    } else { f.tDof.value = null; f.uDof.value = 0; }
    this.pass(this.bright, t.rt.texture, t.bloomA!);
    this.blurPass(t.bloomA!, t.bloomB!);
    f.tScene.value = t.rt.texture;
    f.tBloom.value = t.bloomA!.texture;
    this.quad.material = this.final;
    r.setRenderTarget(null);
    this.quad.render(r);
  }

  /** A two-pass blur of a, through b and back into a; `spread` widens the taps (in a's texels). */
  private blurPass(a: THREE.WebGLRenderTarget, b: THREE.WebGLRenderTarget, spread = 1) {
    this.blur.uniforms.uDir.value.set(spread / a.width, 0);
    this.pass(this.blur, a.texture, b);
    this.blur.uniforms.uDir.value.set(0, spread / a.height);
    this.pass(this.blur, b.texture, a);
  }

  private pass(mat: THREE.ShaderMaterial, src: THREE.Texture, to: THREE.WebGLRenderTarget) {
    mat.uniforms.tSrc.value = src;
    this.quad.material = mat;
    this.renderer.setRenderTarget(to);
    this.quad.render(this.renderer);
  }

  /** Every program the next frames will want, compiled now: the scene as the tier draws it (into the HDR target or
   *  to the screen), the post passes, and the sun's depth programs (Sheep Tag's warmShadows: the shadow pass runs once
   *  with each draw swapped for a compile, one per kind of caster). Call at load, on a tier change and when the 3D
   *  armies arrive. */
  warm() {
    const r = this.renderer, prev = r.getRenderTarget(), t = this.t;
    // what shows only now and then (rain, snow, the lanterns' pools: userData.warm) is shown for the compile, so its
    // first frame doesn't hitch either (Sheep Tag's stand-ins)
    const shown: THREE.Object3D[] = [];
    this.scene.traverse((o) => { if (!o.visible && o.userData.warm) { o.visible = true; shown.push(o); } });
    try {
      r.setRenderTarget(t.rt);
      r.compile(this.scene, this.camera);
      if (t.rt) {
        const passes: [THREE.ShaderMaterial, THREE.WebGLRenderTarget | null][] = [[this.bright, t.bloomA], [this.blur, t.bloomA], [this.final, null]];
        if (t.dofA) passes.push([this.down, t.dofA]);
        if (t.aoA) passes.push([this.ao, t.aoA], [this.blur, t.aoA]);
        for (const [m, to] of passes) {
          this.quad.material = m; r.setRenderTarget(to); r.compile((this.quad as unknown as { _mesh: THREE.Mesh })._mesh, this.camera);
        }
      }
    } finally { r.setRenderTarget(prev); for (const o of shown) o.visible = false; }
    if (!r.shadowMap.enabled || !this.sun.castShadow) return;
    const sm = r.shadowMap, seen = new Set<string>(), draw = r.renderBufferDirect;
    r.renderBufferDirect = (_cam, _scene, _geo, material, object) => {
      const key = shadowVariant(material, object);
      if (seen.has(key)) return;
      seen.add(key);
      const mesh = object as THREE.Mesh, keep = mesh.material, kids = mesh.children;
      mesh.material = material; mesh.children = [];
      try { r.compile(mesh, this.camera, this.scene); } finally { mesh.material = keep; mesh.children = kids; }
    };
    // the shadow pass draws with three's empty scene (no fog), and fog is part of a program's key
    const fog = this.scene.fog;
    this.scene.fog = null;
    try { sm.render([this.sun], this.scene, this.camera); } catch { /* the first frame compiles them */ } finally { r.renderBufferDirect = draw; this.scene.fog = fog; }
  }
}
